/**
 * FRONT-DESK-POSSIBLE-MATCH-SURFACE-01 — the lead -> patient crossmatch
 * advisory surface, mounted on the lead / Lead 360 DETAIL page (owner
 * detail + front-desk workspace detail).
 *
 * Two parts, the repo idiom (Lead360Panel / RequirePatientMaster):
 *   - LeadPatientCrossmatchView — PURE (no fetch, no hooks): renders the
 *     honest copy per verdict, or NOTHING (null) when disabled or the
 *     verdict is not_available. Unit-tested via react-dom/server.
 *   - LeadPatientCrossmatchNotice — self-fetching wrapper: reads the
 *     server-computed `me.patientMasterEnabled` signal and ONLY fetches +
 *     renders when it is on. With the flag off it renders null AND its
 *     effect early-returns, so NO request is ever issued (fail-closed).
 *
 * DISPLAY ONLY: this surface never links, reveals, merges, or creates. It
 * renders a tone chip + text and nothing actionable (no button, no link,
 * no click handler). The verdict carries no PHI and no patient id;
 * resolving a match to a named patient is the SEPARATE contact-reveal
 * package (#102).
 */

import { useEffect, useState } from 'react';

import { fetchLeadPatientCrossmatch } from '@/dashboard/api';
import { useDashboardMeContext } from '@/dashboard/me-context';
import {
  describeLeadCrossmatch,
  isLeadCrossmatchEnabled,
  type LeadPatientCrossmatchResult,
} from '@/dashboard/patients/lead-patient-crossmatch-view';

/**
 * PURE presentational view. `enabled` is the flag-gate decision; `result`
 * is the fetched verdict (or null while loading / on error). Renders the
 * honest advisory line, or null when there is nothing to show — flag off,
 * not yet loaded, or not_available (never a false "no patient").
 */
export function LeadPatientCrossmatchView({
  enabled,
  result,
}: {
  enabled: boolean;
  result: LeadPatientCrossmatchResult | null;
}): JSX.Element | null {
  if (!enabled) return null;
  const display = describeLeadCrossmatch(result);
  if (display === null) return null;
  return (
    <aside
      className="lead-crossmatch"
      data-testid="lead-crossmatch-notice"
      data-verdict={display.verdict}
      role="note"
    >
      <span className={display.chipClass}>{display.text}</span>
    </aside>
  );
}

export interface LeadPatientCrossmatchNoticeProps {
  /** The lead's opaque safe id (the Lead360 safeLeadId). */
  safeLeadId: string;
  /**
   * Injected fetcher (keeps the fetch path unit-testable). Defaults to the
   * real safe endpoint. MUST stay a stable reference — the effect re-runs
   * when its identity changes.
   */
  fetcher?: (safeLeadId: string) => Promise<LeadPatientCrossmatchResult>;
  /**
   * LEAD-TRUTH-AND-DEPENDENT-STATE-CORRECTIVE-V1 (Fix 2): the host's
   * mutation counter. A bump re-runs the advisory read so a
   * convert-to-patient never leaves a stale "possible existing patient"
   * notice beside a freshly linked record. Omitted (undefined) keeps the
   * mount-only behavior byte-identical for other hosts.
   */
  reloadToken?: number;
}

/**
 * Self-fetching, fault-isolated wrapper. Mounted on the lead detail
 * surface with the lead's opaque safe id; reads the flag from the
 * dashboard `/me` context and renders the pure view.
 */
export default function LeadPatientCrossmatchNotice({
  safeLeadId,
  fetcher = fetchLeadPatientCrossmatch,
  reloadToken,
}: LeadPatientCrossmatchNoticeProps): JSX.Element | null {
  const meCtx = useDashboardMeContext();
  const enabled = isLeadCrossmatchEnabled(meCtx?.me ?? null);
  const [result, setResult] = useState<LeadPatientCrossmatchResult | null>(null);

  useEffect(() => {
    // FAIL-CLOSED: with the flag off (or no lead in view) we NEVER issue a
    // fetch — the surface is dark, not merely hidden.
    if (!enabled || safeLeadId.trim() === '') {
      setResult(null);
      return undefined;
    }
    let cancelled = false;
    void fetcher(safeLeadId)
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch(() => {
        // Advisory only: any failure renders nothing — never a false
        // "no existing patient match", never a broken detail page.
        if (!cancelled) setResult(null);
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, safeLeadId, fetcher, reloadToken]);

  return <LeadPatientCrossmatchView enabled={enabled} result={result} />;
}
